import { useEffect, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CheckSquare, FileText, Search as SearchIcon } from 'lucide-react'
import { Card, EmptyState, SearchInput, SkeletonList, Tabs } from '@/components/ui'
import { PageHeader, PlatformIcon, QueryBoundary, SpeakerChip } from '@/components/domain'
import { api } from '@/internationalization/api-client'
import { qk } from '@/internationalization/query-keys'
import { formatDayLabel, formatTimecode } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import { useDebouncedValue } from '@/hooks'
import type { ActionItem, Meeting, TranscriptSegment } from '@/types'

interface SearchResults {
  meetings: Meeting[]
  segments: (TranscriptSegment & { meetingId: string; meetingTitle: string; startsAt: string })[]
  actions: ActionItem[]
}

/** Wraps each occurrence of the term in a <mark>. Case-insensitive, no regex injection. */
function highlight(text: string, term: string) {
  if (!term) return text
  const lower = text.toLowerCase()
  const needle = term.toLowerCase()
  const parts: ReactNode[] = []
  let cursor = 0
  let index = lower.indexOf(needle)
  while (index !== -1) {
    parts.push(text.slice(cursor, index))
    parts.push(
      <mark key={index} className="rounded bg-warning-subtle px-0.5 text-fg">
        {text.slice(index, index + term.length)}
      </mark>,
    )
    cursor = index + term.length
    index = lower.indexOf(needle, cursor)
  }
  parts.push(text.slice(cursor))
  return parts
}

/**
 * Cross-workspace search over transcripts, titles and action items.
 *
 * The query lives in the URL so a search is a link. Transcript hits are the
 * default tab: searching a meeting title is what the meetings list is for, and
 * "what did we say about X" is the question this screen exists to answer.
 */
export function SearchPage() {
  const t = useT()
  const [searchParams, setSearchParams] = useSearchParams()
  const [term, setTerm] = useState(searchParams.get('q') ?? '')
  const [tab, setTab] = useState<'segments' | 'meetings' | 'actions'>('segments')
  const debounced = useDebouncedValue(term, 300)

  useEffect(() => {
    const next = new URLSearchParams()
    if (debounced) next.set('q', debounced)
    setSearchParams(next, { replace: true })
  }, [debounced, setSearchParams])

  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.search(debounced, {}),
    queryFn: () => api.get<SearchResults>('/search', { query: { q: debounced } }),
    enabled: debounced.trim().length >= 2,
  })

  const isIdle = debounced.trim().length < 2

  return (
    <div>
      <PageHeader
        title={t('search.title')}
        description={t('search.description')}
        below={
          <div className="space-y-3">
            <SearchInput
              value={term}
              onChange={setTerm}
              autoFocus
              placeholder={t('search.inputPlaceholder')}
              className="max-w-xl"
            />
            {!isIdle && (
              <Tabs
                variant="pill"
                activeId={tab}
                onChange={(id) => setTab(id as typeof tab)}
                tabs={[
                  { id: 'segments', label: t('search.tab.transcript'), icon: <FileText />, count: data?.segments.length },
                  { id: 'meetings', label: t('search.tab.meetings'), count: data?.meetings.length },
                  { id: 'actions', label: t('search.tab.actions'), icon: <CheckSquare />, count: data?.actions.length },
                ]}
              />
            )}
          </div>
        }
      />

      {isIdle ? (
        <Card>
          <EmptyState
            icon={<SearchIcon />}
            title={t('search.idleTitle')}
            description={t('search.idleBody')}
          />
        </Card>
      ) : (
        <QueryBoundary
          isLoading={isPending}
          error={error}
          onRetry={refetch}
          skeleton={<SkeletonList rows={6} className="sb-panel p-4" />}
        >
          {tab === 'segments' && (
            <Card>
              {data?.segments.length ? (
                <ul className="divide-y divide-border">
                  {data.segments.map((segment) => (
                    <li key={`${segment.meetingId}-${segment.id}`}>
                      <Link
                        to={`/meetings/${segment.meetingId}`}
                        className="block px-4 py-3 transition-colors hover:bg-surface-muted"
                      >
                        <div className="mb-1 flex flex-wrap items-center gap-2 text-sm">
                          <SpeakerChip name={segment.speakerName} label={segment.speakerLabel} />
                          <span className="font-mono text-xs text-fg-subtle">
                            {formatTimecode(segment.startSeconds)}
                          </span>
                          <span className="truncate text-fg-muted">{segment.meetingTitle}</span>
                          <span className="text-fg-subtle">{formatDayLabel(segment.startsAt)}</span>
                        </div>
                        <p className="text-base text-fg">{highlight(segment.text, debounced)}</p>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState
                  title={t('search.noSegmentsTitle')}
                  description={t('search.noSegmentsBody')}
                />
              )}
            </Card>
          )}

          {tab === 'meetings' && (
            <Card>
              {data?.meetings.length ? (
                <ul className="divide-y divide-border">
                  {data.meetings.map((meeting) => (
                    <li key={meeting.id}>
                      <Link
                        to={`/meetings/${meeting.id}`}
                        className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface-muted"
                      >
                        <PlatformIcon platform={meeting.platform} size="sm" />
                        <span className="min-w-0 flex-1 truncate text-base text-fg">
                          {highlight(meeting.title, debounced)}
                        </span>
                        <span className="shrink-0 text-sm text-fg-subtle">
                          {formatDayLabel(meeting.startsAt)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title={t('search.noMeetings')} />
              )}
            </Card>
          )}

          {tab === 'actions' && (
            <Card>
              {data?.actions.length ? (
                <ul className="divide-y divide-border">
                  {data.actions.map((item) => (
                    <li key={item.id} className="px-4 py-3">
                      <p className="text-base text-fg">{highlight(item.text, debounced)}</p>
                      <p className="mt-0.5 text-sm text-fg-subtle">
                        {item.owner?.displayName ?? t('summary.unassigned')} · {item.meetingTitle}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState title={t('search.noActions')} />
              )}
            </Card>
          )}
        </QueryBoundary>
      )}
    </div>
  )
}
